import React, { useState } from "react";

import { InputAdornment, TextField } from "@mui/material";
import { Controller, useController } from "react-hook-form";

import { FormItemsWrap } from "../../molecules/Wrap/Wrap";
import Label from "./Label";

export const NumberInput = ({
  control,
  label,
  name,
  width,
  size,
  readOnly,
  disabled,
  showLabel,
  fullWidth,
  icon,
}) => {
  const { field, fieldState } = useController({
    control: control,
    name: name,
  });
  return (
    <TextField
      type="number"
      fullWidth={fullWidth}
      disabled={disabled}
      variant="outlined"
      size={size ?? "small"}
      inputProps={{
        style: {
          width: width,
        },
      }}
      error={fieldState?.invalid}
      helperText={fieldState?.error?.message}
      placeholder={label}
      value={field.value}
      label={showLabel ? label : undefined}
      {...field}
      InputProps={{
        endAdornment: <InputAdornment position="end">{icon}</InputAdornment>,
      }}
    />
  );
};

export const NumberInputType2 = ({
  control,
  label,
  name,
  width,
  size,
  readOnly,
  required,
  isInherit,
  disabled,
  showLabel,
  fullWidth,
  placeholder,
  icon,
}) => {
  const { field, fieldState } = useController({
    control: control,
    name: name,
  });
  return (
    <FormItemsWrap width={width}>
      <Label label={label} required={required} />

      <TextField
        type="number"
        fullWidth={fullWidth}
        disabled={disabled}
        variant="outlined"
        size={size ?? "small"}
        inputProps={{
          style: {
            width: width,
          },
        }}
        error={fieldState?.invalid}
        helperText={fieldState?.error?.message}
        placeholder={placeholder ? placeholder : ""}
        value={field.value}
        label={showLabel ? label : undefined}
        {...field}
        InputProps={{
          endAdornment: <InputAdornment position="end">{icon}</InputAdornment>,
        }}
      />
    </FormItemsWrap>
  );
};
const blockInvalidChar = (e) => {
  const invalidChars = ["e", "E", "+", "-", "ArrowUp", "ArrowDown"];
  if (invalidChars.includes(e.key)) {
    e.preventDefault();
  }
};

export const NumberInputType3 = ({
  control,
  label,
  name,
  width,
  size,
  disabled,
  showLabel,
  fullWidth,
  endAdornment,
  placeholder,
  startAdornment,
  color,
  fontcolor,
  isInherit,
  icon,
  password,
  required,
}) => {
  const { field, fieldState } = useController({
    control: control,
    name: name,
  });

  return (
    <FormItemsWrap width={width}>
      <Label label={label} required={required} />

      <Controller
        control={control}
        name={name}
        render={({ field: { onChange, value } }) => (
          <TextField
            type={"text"}
            fullWidth={fullWidth}
            disabled={disabled}
            variant="outlined"
            onKeyDown={blockInvalidChar}
            size={size ?? "small"}
            onChange={(e) => {
              const value = e.target.value;
              // Allow only numbers with up to two decimal places
              if (
                (/^\d*\.?\d{0,2}$/.test(value) || value === "") &&
                value.length <= 16
              ) {
                onChange(value);
              }
            }}
            inputProps={{
              style: {
                width: width,
              },
              inputMode: "decimal", // Supports decimal numbers
              pattern: "\\d*\\.?\\d{0,2}", // Allow up to 2 decimal places
            }}
            sx={{
              "& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button": {
                WebkitAppearance: "none",
                margin: 0,
              },
              "& input[type=number]": {
                MozAppearance: "textfield",
              },
            }}
            error={fieldState?.invalid}
            helperText={fieldState?.error?.message}
            value={value}
            label={showLabel ? label : undefined}
            placeholder={placeholder ? placeholder : ""}
            InputProps={{
              endAdornment: endAdornment && (
                <InputAdornment position="end">{endAdornment}</InputAdornment>
              ),
              startAdornment: startAdornment && (
                <InputAdornment position="start">{startAdornment}</InputAdornment>
              ),
              style: {
                background: color ? color : "",
                borderRadius: "7px",
              },
            }}
          />
        )}
      />
    </FormItemsWrap>
  );
};


const formatNumberWithSeparator = (value) => {
  if (value === "" || value === null || value === undefined) {
    return "";
  }

  const numValue = parseFloat(value);
  if (isNaN(numValue)) {
    return "";
  }

  return numValue.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};



const unformatNumber = (formattedValue) => {
  if (!formattedValue) return "";
  const stringValue = String(formattedValue);
  return stringValue.replace(/,/g, "");
};

export const NumberInputType4 = ({
  control,
  label,
  name,
  width,
  size,
  disabled,
  showLabel,
  fullWidth,
  endAdornment,
  placeholder,
  startAdornment,
  color,
  required,
}) => {
  const [isFocused, setIsFocused] = React.useState(false);

  return (
    <FormItemsWrap width={width}>
      {label && <Label label={label} required={required} />}

      <Controller
        control={control}
        name={name}
        render={({ field: { onChange, value, ref }, fieldState }) => {
          const displayValue = isFocused
            ? value ?? ""
            : formatNumberWithSeparator(value);

          return (
            <TextField
              inputRef={ref}
              type="text"
              fullWidth={fullWidth}
              disabled={disabled}
              variant="outlined"
              size={size ?? "small"}
              value={displayValue}
              placeholder={placeholder}
              error={fieldState?.invalid}
              helperText={fieldState?.error?.message}

              onFocus={() => {
                setIsFocused(true);
                onChange(unformatNumber(value));
              }}

              onBlur={() => {
                setIsFocused(false);
                const raw = unformatNumber(value);
                if (raw !== "") {
                  onChange(Number(raw).toFixed(2));
                }
              }}

              onChange={(e) => {
                const rawValue = unformatNumber(e.target.value);
                if (
                  (/^\d*\.?\d{0,2}$/.test(rawValue) || rawValue === "") &&
                  rawValue.length <= 16
                ) {
                  onChange(rawValue);
                }
              }}

              onKeyDown={(e) => {
                const invalidChars = ["e", "E", "+", "-", "ArrowUp", "ArrowDown"];
                if (invalidChars.includes(e.key)) {
                  e.preventDefault();
                }
              }}

              inputProps={{
                style: { width },
                inputMode: "decimal",
              }}

              InputProps={{
                endAdornment: endAdornment && (
                  <InputAdornment position="end">{endAdornment}</InputAdornment>
                ),
                startAdornment: startAdornment && (
                  <InputAdornment position="start">{startAdornment}</InputAdornment>
                ),
                style: {
                  background: color || "",
                  borderRadius: "7px",
                },
              }}
            />
          );
        }}
      />
    </FormItemsWrap>
  );
};
